import axios from "../config/axios"

const API_BASE = "/shipments"

/**
 * Serialize filter params for shipment list/export/widget.
 * Array values use bracket keys (mode_of_payment[]=…) — matches backend _parse_list_param order.
 * Scalar values use plain keys (from_date, to_date, page, etc.).
 */
const serializeShipmentParams = (params = {}) => {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      val.forEach((v) => {
        if (v !== undefined && v !== null && v !== "") {
          qs.append(`${key}[]`, String(v))
        }
      })
    } else if (val !== undefined && val !== null && val !== "") {
      qs.append(key, String(val))
    }
  })
  return qs.toString()
}

const buildShipmentUrl = (path, params = {}) => {
  const queryString = serializeShipmentParams(params)
  return queryString ? `${path}?${queryString}` : path
}

export const shipmentService = {
  // Get all shipments with filters - UPDATED
  getAllShipments: (page = 1, perPage = 20, filters = {}) => {
    const params = {
      page,
      per_page: perPage,
      ...filters,
    }
    return axios.get(buildShipmentUrl(API_BASE, params))
  },

  // Get shipments with filters (alias) - NEW METHOD
  getShipments: (params = {}) => {
    return axios.get(buildShipmentUrl(API_BASE, params))
  },

  // Get today's shipments
  getTodaysShipments: (page = 1, perPage = 20) => {
    return axios.get(`${API_BASE}/today`, {
      params: { page, per_page: perPage },
    })
  },

  // Get single shipment
  getShipment: (shipmentId) => {
    return axios.get(`${API_BASE}/${shipmentId}`)
  },

  // Auto ML options for shipment booking
  getAutoMlOptions: (senderHubId, receiverHubId) => {
    return axios.get(`${API_BASE}/auto-ml/options`, {
      params: {
        sender_location_hub_id: senderHubId,
        receiver_location_hub_id: receiverHubId,
      },
    })
  },

  // Create shipment
  createShipment: (data) => {
    const formData = new FormData()

    Object.keys(data).forEach((key) => {
      if (key === "shipment_media" && Array.isArray(data[key])) {
        data[key].forEach((file) => {
          if (file) formData.append("shipment_media", file)
        })
      } else if (key === "parcel_image_1" || key === "parcel_image_2") {
        // Legacy fields ignored — use shipment_media
      } else if (key === "eway_bill_numbers" || key === "parcels") {
        if (data[key] && Array.isArray(data[key]) && data[key].length > 0) {
          formData.append(key, JSON.stringify(data[key]))
        }
      } else if (data[key] !== null && data[key] !== undefined && data[key] !== "") {
        formData.append(key, data[key])
      }
    })

    return axios.post(API_BASE, formData)
  },

  // Update shipment
  updateShipment: (shipmentId, data) => {
    let formData = data

    if (!(data instanceof FormData)) {
      formData = new FormData()
      const alwaysSendKeys = [
        "consigner_address", "consigner_email", "consigner_gst",
        "consignee_address", "consignee_email", "consignee_gst",
        "sender_address", "sender_email", "sender_gst",
        "receiver_address", "receiver_email", "receiver_gst",
        "invoice_number", "driver_contact", "parcel_detail",
        "shipment_client_id", "mode_of_payment",
      ]
      Object.keys(data).forEach((key) => {
        if (key === "shipment_media" && Array.isArray(data[key])) {
          data[key].forEach((file) => {
            if (file) formData.append("shipment_media", file)
          })
        } else if (key === "remove_media_ids" && Array.isArray(data[key])) {
          if (data[key].length > 0) {
            formData.append("remove_media_ids", JSON.stringify(data[key]))
          }
        } else if (key === "parcel_image_1" || key === "parcel_image_2") {
          // Legacy ignored
        } else if (key === "parcels" || key === "eway_bill_numbers") {
          if (data[key] && Array.isArray(data[key]) && data[key].length > 0) {
            formData.append(key, JSON.stringify(data[key]))
          }
        } else if (alwaysSendKeys.includes(key)) {
          formData.append(key, data[key] ?? "")
        } else if (data[key] !== null && data[key] !== undefined && data[key] !== "") {
          formData.append(key, data[key])
        }
      })
    }

    return axios.put(`${API_BASE}/${shipmentId}`, formData)
  },

  // Delete shipment
  deleteShipment: (shipmentId, deleteReason) => {
    return axios.delete(`${API_BASE}/${shipmentId}`, {
      data: { delete_reason: deleteReason },
    })
  },

  // Download invoice
  downloadInvoice: (shipmentId, includeLabour = true) => {
    return axios.get(`${API_BASE}/${shipmentId}/invoice/download`, {
      params: { include_labour: includeLabour ? "yes" : "no" },
      responseType: "blob",
    })
  },

  // Send invoice via email
  sendInvoiceEmail: (shipmentId, includeLabour = true) => {
    return axios.post(`${API_BASE}/${shipmentId}/invoice/send`, {
      include_labour: includeLabour,
    })
  },

  updateStatus: (shipmentId, status, remarks = "", deliveryReceiver = "", deliveryReceiverImage = null, deliveryReceiverContact = "") => {
    // If status is Delivered and delivery info is provided, use FormData
    if (status === "Delivered" && (deliveryReceiver || deliveryReceiverImage || deliveryReceiverContact)) {
      const formData = new FormData()
      formData.append("status", status)
      formData.append("remarks", remarks)
      
      if (deliveryReceiver) {
        formData.append("delivery_receiver", deliveryReceiver)
      }

      if (deliveryReceiverContact) {
        formData.append("delivery_receiver_contact", deliveryReceiverContact)
      }
      
      if (deliveryReceiverImage) {
        formData.append("delivery_receiver_image", deliveryReceiverImage)
      }
      
      return axios.post(`${API_BASE}/${shipmentId}/status`, formData, {
        headers: { "Content-Type": undefined }
      })
    }
    
    // For other statuses, use regular JSON
    return axios.post(`${API_BASE}/${shipmentId}/status`, { status, remarks })
  },

  getDestinationReceivingAgents: (receiverLocationHubId) =>
    axios.get(`${API_BASE}/destination-receiving-agents`, {
      params: { receiver_location_hub_id: receiverLocationHubId },
    }),

  // Bulk update status with delivery receiver info
  bulkUpdateStatus: (shipmentIds, status, remarks = "", deliveryReceiver = "", deliveryReceiverImage = null, deliveryReceiverContact = "", unloadingCharges = null, loadingCharges = null, deliveryGovIdImage = null, deliveryVehicleNo = "", lastMileHours = "", demurrageAmount = null, receiverAgentUserId = null, receiverAgentByShipment = null) => {
    const hasUnloading = unloadingCharges !== null && unloadingCharges !== undefined && unloadingCharges !== ""
    const hasLoading = loadingCharges !== null && loadingCharges !== undefined && loadingCharges !== ""
    // Delivered always sends multipart so delivery photo and government ID are included
    if (status === "Delivered") {
      const formData = new FormData()
      formData.append("shipment_ids", JSON.stringify(shipmentIds))
      formData.append("status", status)
      formData.append("remarks", remarks || "")

      if (deliveryReceiver) {
        formData.append("delivery_receiver", deliveryReceiver)
      }

      if (deliveryReceiverContact) {
        formData.append("delivery_receiver_contact", deliveryReceiverContact)
      }

      if (deliveryReceiverImage) {
        formData.append("delivery_receiver_image", deliveryReceiverImage)
      }
      if (deliveryGovIdImage) {
        formData.append("delivery_gov_id_image", deliveryGovIdImage)
      }
      if (hasUnloading) {
        formData.append("unloading_charges", String(unloadingCharges))
      }
      if (hasLoading) {
        formData.append("loading_charges", String(loadingCharges))
      }
      if (deliveryVehicleNo) {
        formData.append("delivery_vehicle_no", deliveryVehicleNo)
      }
      if (lastMileHours) {
        formData.append("last_mile_hours", String(lastMileHours))
      }
      if (demurrageAmount !== null && demurrageAmount !== undefined && demurrageAmount !== "") {
        formData.append("demurrage_amount", String(demurrageAmount))
      }
      if (receiverAgentByShipment && Object.keys(receiverAgentByShipment).length) {
        formData.append(
          "receiver_agent_by_shipment",
          JSON.stringify(receiverAgentByShipment),
        )
      } else if (receiverAgentUserId) {
        formData.append("receiver_agent_user_id", String(receiverAgentUserId))
      }

      return axios.post(`${API_BASE}/bulk_status_update`, formData)
    }

    const payload = {
      shipment_ids: shipmentIds,
      status: status,
      remarks: remarks,
    }
    if (hasUnloading) {
      payload.unloading_charges = Number(unloadingCharges)
    }
    if (hasLoading) {
      payload.loading_charges = Number(loadingCharges)
    }
    if (receiverAgentByShipment && Object.keys(receiverAgentByShipment).length) {
      payload.receiver_agent_by_shipment = receiverAgentByShipment
    } else if (receiverAgentUserId) {
      payload.receiver_agent_user_id = receiverAgentUserId
    }
    return axios.post(`${API_BASE}/bulk_status_update`, payload)
  },

  revertDelivery: (shipmentId, remarks = '') => {
    return axios.post(`${API_BASE}/${encodeURIComponent(shipmentId)}/revert-delivery`, {
      remarks,
    })
  },

  revertReceive: (shipmentId, remarks = '') => {
    return axios.post(`${API_BASE}/${encodeURIComponent(shipmentId)}/revert-receive`, {
      remarks,
    })
  },

  verifyDeliveryOtp: (shipmentId, otp) => {
    return axios.post(`${API_BASE}/${shipmentId}/delivery-otp/verify`, { otp })
  },

  resendDeliveryOtp: (shipmentId) => {
    return axios.post(`${API_BASE}/${shipmentId}/delivery-otp/resend`)
  },

  verifyDeliveryOtpBulk: (items) => {
    return axios.post(`${API_BASE}/delivery-otp/verify-bulk`, { items })
  },

  // Get tracking information
  getTrackingInfo: (shipmentId) => {
    return axios.get(`/tracking/${shipmentId}`)
  },

  // Get distinct users
  getDistinctUsers: () => {
    return axios.get(`${API_BASE}/get_distinct_users`)
  },

  // Get statistics
  getStatistics: () => {
    return axios.get(`${API_BASE}/statistics`)
  },

  // Export PDF
  exportPDF: (params = {}) => {
    return axios.get(buildShipmentUrl(`${API_BASE}/export/pdf`, params), {
      responseType: "blob",
    })
  },

  // Get location hubs by state
  getLocationHubsByState: (state) => {
    return axios.get("/location-hubs", {
      params: { state },
    })
  },

  // Get all location hubs (full list for dropdowns)
  getAllLocationHubs: () => {
    return axios.get("/location-hubs", {
      params: { all: true },
    })
  },

  addLabourCharge: (shipmentId, amount, paymentMode) => {
    return axios.post(`${API_BASE}/${shipmentId}/labour-charge`, {
      labour_charge: amount,
      payment_mode: paymentMode,
    })
  },

  addDeliveryCharge: (shipmentId, amount, paymentMode) => {
    return axios.post(`${API_BASE}/${shipmentId}/delivery-charge`, {
      delivery_charge: amount,
      payment_mode: paymentMode,
    })
  }, 
  
  // Print receipt
  printReceipt: (shipmentId, event, { force = false, layout } = {}) => {
    const params = { format: 'html', _t: Date.now() }
    if (event) params.event = event
    if (force) params.force = 1
    if (layout) params.layout = layout
    return axios.get(`${API_BASE}/${shipmentId}/print-receipt`, { params })
  },

  // Get E-way bills for a shipment
  getEwayBills: (shipmentId) => {
    return axios.get(`/shipments/eway-bills/${shipmentId}`)
  },

  printEwayBill: (shipmentId, billId = null) => {
    const params = billId ? { bill_id: billId } : {}
    return axios.get(`/shipments/eway-bills/print/${shipmentId}`, {
      params,
      responseType: 'blob',
    })
  },

  // Add E-way bills to a shipment
  addEwayBills: (shipmentId, ewayBillNumbers) => {
    return axios.post(`/shipments/eway-bills/${shipmentId}`, {
      eway_bill_numbers: ewayBillNumbers
    })
  },

  // PeriOne: generate e-way bill from shipment data
  generateEwayBill: (shipmentId, payload = {}) => {
    return axios.post(`/shipments/eway-bills/generate/${shipmentId}`, payload)
  },

  // PeriOne: look up an existing e-way bill (does not generate)
  lookupEwayBill: (ewbNo, params = {}) => {
    return axios.get('/shipments/eway-bills/lookup', {
      params: { ewbNo, ...params },
    })
  },

  // PeriOne: update Part-B / vehicle
  updateEwayBillVehicle: (ewayBillId, payload = {}) => {
    return axios.post(`/shipments/eway-bills/update-vehicle/${ewayBillId}`, payload)
  },

  // PeriOne: cancel e-way bill
  cancelEwayBill: (ewayBillId, payload = {}) => {
    return axios.post(`/shipments/eway-bills/cancel/${ewayBillId}`, payload)
  },

  // PeriOne: remote list by date (DD/MM/YYYY)
  listRemoteEwayBills: (date, params = {}) => {
    return axios.get('/shipments/eway-bills/remote-list', {
      params: { date, ...params },
    })
  },

  // PeriOne GST taxpayer search (only when company GST verification is enabled)
  searchGstin: (gstin, params = {}) => {
    return axios.get('/shipments/gst/search', {
      params: { gstin, ...params },
    })
  },

  // Get customer suggestions by contact number only (full 10-digit mobile)
  getCustomerSuggestions: (contact = '') => {
    const digits = String(contact || '').replace(/\D/g, '')
    if (digits.length < 10) {
      return Promise.reject(new Error('Contact must be at least 10 digits'))
    }
    return axios.get(`${API_BASE}/customer-suggestions`, {
      params: { contact: digits.slice(-10) },
    })
  },

  /**
   * Merge sender/receiver suggestion lists (same person may only exist on one side).
   * Dedupes by contact + name + address.
   */
  mergeCustomerSuggestions: (...lists) => {
    const seen = new Set()
    const out = []
    for (const list of lists) {
      if (!Array.isArray(list)) continue
      for (const item of list) {
        if (!item) continue
        const pairedName = item.paired_receiver?.name || item.paired_sender?.name || ''
        const pairedAddress = item.paired_receiver?.address || item.paired_sender?.address || ''
        const key = [
          String(item.contact || '').replace(/\D/g, '').slice(-10),
          String(item.name || '').trim().toLowerCase(),
          String(item.address || '').trim().toLowerCase(),
          String(pairedName).trim().toLowerCase(),
          String(pairedAddress).trim().toLowerCase(),
        ].join('|')
        if (seen.has(key)) continue
        seen.add(key)
        out.push({
          name: item.name || '',
          contact: item.contact || '',
          email: item.email || '',
          address: item.address || '',
          gst: item.gst || '',
          paired_receiver: item.paired_receiver || null,
          paired_sender: item.paired_sender || null,
        })
      }
    }
    return out
  },

  // Delete E-way bill
  deleteEwayBill: (ewayBillId) => {
    return axios.delete(`/shipments/eway-bills/delete/${ewayBillId}`)
  },

  // Download single QR code as PDF
  downloadQRCode: (shipmentId) => {
    return axios.get(`${API_BASE}/${shipmentId}/qr-code/download`, {
      responseType: "blob",
    })
  },

  // Preview QR code (blob, for opening in new tab)
  getQRCode: (shipmentId, template) => {
    return axios.get(`${API_BASE}/${shipmentId}/qr-code`, {
      responseType: "blob",
      params: template ? { template } : undefined,
    })
  },

  getQRCodeHtml: (shipmentId, template) => {
    // Blob keeps a PDF body intact when an older server ignores output=html.
    return axios.get(`${API_BASE}/${shipmentId}/qr-code`, {
      responseType: "blob",
      params: {
        output: "html",
        ...(template ? { template } : {}),
      },
    })
  },

  // Preview shipping label PDF (priority-mail style with QR)
  getShippingLabel: (shipmentId) => {
    return axios.get(`${API_BASE}/${shipmentId}/shipping-label`, {
      responseType: "blob",
    })
  },

  downloadShippingLabel: (shipmentId) => {
    return axios.get(`${API_BASE}/${shipmentId}/shipping-label/download`, {
      responseType: "blob",
    })
  },

  bulkDownloadShippingLabelsFiltered: async (params = {}) => {
    try {
      return await axios.get(buildShipmentUrl(`${API_BASE}/shipping-labels/bulk-export`, params), {
        responseType: "blob",
      })
    } catch (err) {
      if (err.response?.status !== 404) {
        throw err
      }
      const listParams = { ...params, page: 1, per_page: 5000 }
      const listRes = await axios.get(buildShipmentUrl(API_BASE, listParams))
      const rows =
        listRes.data?.data ||
        listRes.data?.shipments ||
        listRes.data?.items ||
        []
      const shipmentIds = (Array.isArray(rows) ? rows : [])
        .map((s) => s.shipment_id)
        .filter(Boolean)
      if (!shipmentIds.length) {
        const noMatch = new Error("No shipments match the current filters")
        noMatch.response = { data: { error: noMatch.message }, status: 404 }
        throw noMatch
      }
      return axios.post(
        `${API_BASE}/shipping-labels/bulk`,
        { shipment_ids: shipmentIds },
        { responseType: "blob" }
      )
    }
  },

  // Bulk download QR codes as combined PDF (explicit shipment IDs)
  bulkDownloadQRCodes: (shipmentIds) => {
    return axios.post(`${API_BASE}/qr-codes/bulk`, { shipment_ids: shipmentIds }, {
      responseType: "blob",
    })
  },

  // Bulk download QR codes for all shipments matching list filters (no pagination)
  bulkDownloadQRCodesFiltered: async (params = {}) => {
    try {
      return await axios.get(buildShipmentUrl(`${API_BASE}/qr-codes/bulk-export`, params), {
        responseType: "blob",
      })
    } catch (err) {
      // Fallback when bulk-export route unavailable (stale server): fetch IDs then POST bulk
      if (err.response?.status !== 404) {
        throw err
      }
      const listParams = { ...params, page: 1, per_page: 5000 }
      const listRes = await axios.get(buildShipmentUrl(API_BASE, listParams))
      const rows =
        listRes.data?.data ||
        listRes.data?.shipments ||
        listRes.data?.items ||
        []
      const shipmentIds = (Array.isArray(rows) ? rows : [])
        .map((s) => s.shipment_id)
        .filter(Boolean)
      if (!shipmentIds.length) {
        const noMatch = new Error("No shipments match the current filters")
        noMatch.response = { data: { error: noMatch.message }, status: 404 }
        throw noMatch
      }
      return axios.post(
        `${API_BASE}/qr-codes/bulk`,
        { shipment_ids: shipmentIds },
        { responseType: "blob" }
      )
    }
  },

  // Get booked shipments grouped by sender location (multi-select filters via bracket keys)
  getBookedByLocation: (params = {}) => {
    return axios.get(buildShipmentUrl(`${API_BASE}/booked-by-sender-location`, params))
  },

  getBookedByAgent: (params = {}) => {
    return axios.get(buildShipmentUrl(`${API_BASE}/booked-by-agent`, params))
  },

  // Bulk download QR codes as ZIP (separate PDFs)
  bulkDownloadQRCodesZip: (shipmentIds) => {
    return axios.post(`${API_BASE}/qr-codes/bulk-zip`, { shipment_ids: shipmentIds }, {
      responseType: "blob",
    })
  },

}